import { __decorate, __metadata } from 'tslib';
import { Injectable, Component, HostListener, ViewEncapsulation, Input, Directive, ElementRef, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { UIKitIconsModule } from '@uikit/components/uk-icons';
import { polyfillPrepend, KEYS } from '@uikit/components/helpers';
import { Subject } from 'rxjs';

var ModalEvents;
(function (ModalEvents) {
    ModalEvents["close"] = "close";
    ModalEvents["confirm"] = "confirm";
    ModalEvents["dismiss"] = "dismiss";
})(ModalEvents || (ModalEvents = {}));

var UkModalService = /** @class */ (function () {
    function UkModalService(overlay) {
        this.overlay = overlay;
        this.openModal = false;
        this._data = {};
        // Add prepend method to document/window for better compatibility with IE
        polyfillPrepend();
        this.$handler = new Subject();
        this.overlayRef = this.overlay.create({
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop'
        });
    }
    /**
     * Create a new modal
     * @param data            Data to show in modal.
     * @param hasBackdrop     Close modal on backdrop click.
     * @param component       Custom component for modal.
     * @returns ComponentRef Instance
     */
    UkModalService.prototype.createModal = function (data, hasBackdrop, component) {
        var _this = this;
        if (data === void 0) { data = null; }
        if (hasBackdrop === void 0) { hasBackdrop = true; }
        if (!this.openModal) {
            // Assign data before render the Modal Component
            (data) ? this.data = data : this.data = {};
            // Choose Generic or Custom Modal
            var componentTemplate = (component) ? component : UkGenericModalComponent;
            // Create and Attach the Modal Component
            var modal = new ComponentPortal(componentTemplate);
            var componentReference = this.overlayRef.attach(modal);
            this.openModal = !this.openModal;
            // Subscribe to Backdrop Click to dismiss Modal on Click
            if (hasBackdrop) {
                this.backdropSubscription = this.overlayRef.backdropClick().subscribe(function (_) {
                    _this.backdropSubscription.unsubscribe();
                    _this.closeModal(ModalEvents.dismiss);
                });
            }
            return componentReference.instance;
        }
    };
    /**
     * Close the modal
     * @param event       Data to show in modal.
     * @param value       Close modal on backdrop click.
     */
    UkModalService.prototype.closeModal = function (event, value) {
        if (this.openModal) {
            this.openModal = !this.openModal;
            this.overlayRef.detach();
            this.$handler.next({ event: event, value: value });
        }
    };
    Object.defineProperty(UkModalService.prototype, "data", {
        get: function () { return this._data; },
        // Content Generic modal
        set: function (values) { this._data = values; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UkModalService.prototype, "customValues", {
        get: function () { return this._data.CustomValues; },
        set: function (values) { this._data.CustomValues = values; },
        enumerable: true,
        configurable: true
    });
    UkModalService = __decorate([
        Injectable(),
        __metadata("design:paramtypes", [Overlay])
    ], UkModalService);
    return UkModalService;
}());

var UkGenericModalComponent = /** @class */ (function () {
    function UkGenericModalComponent(modalService) {
        this.modalService = modalService;
    }
    Object.defineProperty(UkGenericModalComponent.prototype, "data", {
        get: function () { return this.modalService.data; },
        enumerable: true,
        configurable: true
    });
    UkGenericModalComponent = __decorate([
        Component({
            selector: 'uk-modal',
            template: "<uk-modal-container>\n  <uk-modal-header>\n    <a *ngIf=\"data.Icon\" ukIcon=\"{{data.Icon}}\" style=\"font-size:36px; font-weight: 100;\"></a>\n    <h3 *ngIf=\"data.HeaderTitle\" class=\"center\">{{data.HeaderTitle}}</h3>\n  </uk-modal-header>\n  <uk-modal-body>\n    <p class=\"uk-text-center\" ukModalData></p>\n  </uk-modal-body>\n  <uk-modal-footer>\n    <div class=\"uk-btn-container center\">\n      <button class=\"uk-btn solid primary\" ukModalData ukModalEvent=\"close\"></button>\n    </div>\n  </uk-modal-footer>\n</uk-modal-container>"
        }),
        __metadata("design:paramtypes", [UkModalService])
    ], UkGenericModalComponent);
    return UkGenericModalComponent;
}());

var UkTemplateModalComponent = /** @class */ (function () {
    function UkTemplateModalComponent(modalService) {
        var _this = this;
        this.modalService = modalService;
        this.closeEvent = this.modalService.$handler.subscribe(function (_) {
            _this.close();
        });
    }
    UkTemplateModalComponent.prototype.handleKeydown = function (event) {
        if (event.key === KEYS.Escape) {
            this.modalService.closeModal(ModalEvents.dismiss);
        }
    };
    UkTemplateModalComponent.prototype.close = function () {
        this.closeEvent.unsubscribe();
        this.modalService.closeModal(ModalEvents.close);
    };
    Object.defineProperty(UkTemplateModalComponent.prototype, "values", {
        get: function () {
            return (this.modalService.customValues) ?
                this.modalService.customValues : {};
        },
        enumerable: true,
        configurable: true
    });
    __decorate([
        HostListener('document:keydown', ['$event']),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [KeyboardEvent]),
        __metadata("design:returntype", void 0)
    ], UkTemplateModalComponent.prototype, "handleKeydown", null);
    UkTemplateModalComponent = __decorate([
        Component({
            selector: 'uk-modal-container',
            template: "\n<div class=\"center\">\n  <div class=\"uk-modal uk-shadow-5\">\n    <div class=\"uk-modal__header\">\n      <div class=\"uk-modal__header__close\">\n        <a ukIcon=\"close\" ukModalEvent=\"close\"></a>\n      </div>\n      <div class=\"uk-modal__header__content uk-text-center\">\n        <!-- Here will be the HEADER -->\n        <ng-content select=\"uk-modal-header\"></ng-content>\n      </div>\n    </div>\n    <div class=\"uk-modal__main\">\n      <!-- Here will be the BODY(text, forms, etc.) -->\n      <ng-content select=\"uk-modal-body\"></ng-content>\n    </div>\n    <div class=\"uk-modal__footer\">\n      <!-- Here will be the FOOTER(buttons) -->\n      <ng-content select=\"uk-modal-footer\"></ng-content>\n    </div>\n  </div>\n</div>\n",
            encapsulation: ViewEncapsulation.None
        }),
        __metadata("design:paramtypes", [UkModalService])
    ], UkTemplateModalComponent);
    return UkTemplateModalComponent;
}());

// Directive to handle Events
var UkModalEventsDirective = /** @class */ (function () {
    function UkModalEventsDirective(modalService) {
        this.modalService = modalService;
    }
    UkModalEventsDirective.prototype.handleClick = function () {
        this.modalService.closeModal(this.ukModalEvent, this.ukModalValues);
    };
    __decorate([
        Input(),
        __metadata("design:type", String)
    ], UkModalEventsDirective.prototype, "ukModalEvent", void 0);
    __decorate([
        Input(),
        __metadata("design:type", Object)
    ], UkModalEventsDirective.prototype, "ukModalValues", void 0);
    __decorate([
        HostListener('click'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], UkModalEventsDirective.prototype, "handleClick", null);
    UkModalEventsDirective = __decorate([
        Directive({
            selector: '[ukModalEvent]'
        }),
        __metadata("design:paramtypes", [UkModalService])
    ], UkModalEventsDirective);
    return UkModalEventsDirective;
}());

// Directive to change innerText in tag according to tagName
var UkModalDataDirective = /** @class */ (function () {
    function UkModalDataDirective(_ts, el) {
        this._ts = _ts;
        this.el = el;
    }
    UkModalDataDirective.prototype.ngAfterContentInit = function () {
        var elem = this.el.nativeElement;
        var values = this._ts.data;
        if (values && values.Content) {
            switch (elem.tagName) {
                case 'BUTTON':
                    elem.innerText = values.Content.ButtonMessage || '';
                    break;
                default:
                    elem.innerText = values.Content.ContentMessage || '';
                    break;
            }
        }
    };
    UkModalDataDirective = __decorate([
        Directive({ selector: '[ukModalData]' }),
        __metadata("design:paramtypes", [UkModalService,
            ElementRef])
    ], UkModalDataDirective);
    return UkModalDataDirective;
}());

// Directive to create a modal based on click on an HTML Tag
var UkModalTriggerDirective = /** @class */ (function () {
    function UkModalTriggerDirective(modalService) {
        this.modalService = modalService;
        this.ukModalTriggerHasBackdrop = true;
    }
    UkModalTriggerDirective.prototype.set = function (values) {
        this.ukModalTriggerValues = values;
    };
    UkModalTriggerDirective.prototype.handleClick = function () {
        // tslint:disable: curly
        if (!this.ukModalTrigger)
            throw new Error('Component is required on ukModalTrigger');
        var modal = this.modalService.createModal(null, this.ukModalTriggerHasBackdrop, this.ukModalTrigger);
        if (modal === null)
            throw new Error('Error instantiating modal');
        this.modalService.customValues = this.ukModalTriggerValues;
    };
    __decorate([
        Input(),
        __metadata("design:type", Component)
    ], UkModalTriggerDirective.prototype, "ukModalTrigger", void 0);
    __decorate([
        Input(),
        __metadata("design:type", Object)
    ], UkModalTriggerDirective.prototype, "ukModalTriggerHasBackdrop", void 0);
    __decorate([
        Input(),
        __metadata("design:type", Object)
    ], UkModalTriggerDirective.prototype, "ukModalTriggerValues", void 0);
    __decorate([
        HostListener('click'),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], UkModalTriggerDirective.prototype, "handleClick", null);
    UkModalTriggerDirective = __decorate([
        Directive({
            selector: '[ukModalTrigger]'
        }),
        __metadata("design:paramtypes", [UkModalService])
    ], UkModalTriggerDirective);
    return UkModalTriggerDirective;
}());

var UkModalModule = /** @class */ (function () {
    function UkModalModule() {
    }
    UkModalModule = __decorate([
        NgModule({
            imports: [
                UIKitIconsModule,
                CommonModule,
                OverlayModule,
                PortalModule
            ],
            declarations: [
                // Components
                UkGenericModalComponent,
                UkTemplateModalComponent,
                // Directives
                UkModalEventsDirective,
                UkModalDataDirective,
                UkModalTriggerDirective
            ],
            exports: [
                UkGenericModalComponent,
                UkTemplateModalComponent,
                UkModalEventsDirective,
                UkModalDataDirective,
                UkModalTriggerDirective
            ],
            entryComponents: [
                UkGenericModalComponent,
                UkTemplateModalComponent
            ],
            providers: [
                UkModalService
            ],
            schemas: [NO_ERRORS_SCHEMA]
        })
    ], UkModalModule);
    return UkModalModule;
}());

export { ModalEvents, UkModalModule, UkModalService, UkGenericModalComponent as ɵa, UkTemplateModalComponent as ɵb, UkModalEventsDirective as ɵc, UkModalDataDirective as ɵd, UkModalTriggerDirective as ɵe };
//# sourceMappingURL=uikit-components-uk-modal.js.map
